import { Insignia, type Tono } from "@/componentes/ui/Insignia"
import { Cabecera, Cuerpo, Fila, Tabla, Td, Th } from "@/componentes/ui/Tabla"
import { comoFecha, hoyLocal } from "@/lib/fechas"
import {
  diasDeVida,
  ESTADOS,
  type EstadoTarea,
  type PrioridadTarea,
  type Tarea,
  TIPOS,
} from "./TarjetaTarea"

/**
 * Las mismas tareas, en tabla.
 *
 * ⚠ **No es el tablero con otra piel.** El tablero sirve para mover cinco tarjetas;
 * esto sirve para repasar cincuenta, y por eso enseña de golpe lo que la tarjeta
 * resume: tipo, prioridad, dueño, vencimiento y antigüedad en la misma fila.
 *
 * ⚠ **No hay ordenación por columna, y es a propósito.** El servidor ordena por
 * vencimiento y luego por prioridad, y pagina. Ordenar aquí las cien filas de la
 * página diría «las más urgentes primero» sobre un recorte que ya venía ordenado de
 * otra forma — una mentira que sólo se nota el día que hay más de cien. Cuando haga
 * falta ordenar por otra cosa, se le pide al servidor.
 *
 * ⚠ **Sin arrastre y sin animación.** El cambio de estado va por el mismo selector
 * que en la tarjeta, y las filas no se animan al releer: el sondeo de 60 s haría
 * temblar la tabla cada minuto.
 *
 * ⚠ **Las filas van en árbol, no en lista plana**, con el mismo idioma visual que el
 * organigrama de `organizacional`: tronco vertical y codo redondeado hacia cada hijo.
 * Ordenadas por vencimiento a secas, una subtarea aparecía en cualquier renglón lejos
 * de su macro y se leía como trabajo independiente — que es como acaba repartiéndose
 * dos veces. El árbol se arma con lo que hay EN LA PÁGINA: una subtarea cuya macro se
 * quedó fuera del filtro se pinta en la raíz, porque esconderla sería peor que
 * pintarla suelta.
 *
 * ⚠ **El orden del servidor se respeta dentro de cada rama.** No se reordena aquí:
 * lo único que se hace es agrupar cada hija bajo su madre.
 */

/** Las macros en el orden del servidor, cada una seguida de sus hijas. */
function enArbol(tareas: Tarea[]): { tarea: Tarea; hija: boolean; ultima: boolean }[] {
  const hijasDe = new Map<number, Tarea[]>()
  const presentes = new Set(tareas.map((t) => t.id))
  for (const t of tareas) {
    if (t.tarea_padre_id !== null && presentes.has(t.tarea_padre_id)) {
      const hermanas = hijasDe.get(t.tarea_padre_id) ?? []
      hermanas.push(t)
      hijasDe.set(t.tarea_padre_id, hermanas)
    }
  }
  const filas: { tarea: Tarea; hija: boolean; ultima: boolean }[] = []
  for (const t of tareas) {
    // Una hija ya la pinta su madre. Si la madre no está en la página, esta tarea
    // ES una raíz para esta vista.
    if (t.tarea_padre_id !== null && presentes.has(t.tarea_padre_id)) continue
    filas.push({ tarea: t, hija: false, ultima: false })
    const hijas = hijasDe.get(t.id) ?? []
    hijas.forEach((h, i) => {
      filas.push({ tarea: h, hija: true, ultima: i === hijas.length - 1 })
    })
  }
  return filas
}

/** Sólo `URGENTE` y `ALTA` se tiñen: si todo destaca, nada destaca. */
const TONO_PRIORIDAD: Record<PrioridadTarea, Tono> = {
  URGENTE: "peligro",
  ALTA: "aviso",
  MEDIA: "neutro",
  BAJA: "neutro",
}

const ETIQUETA_TIPO = new Map(TIPOS.map((t) => [t.valor, t.etiqueta]))

type Props = {
  tareas: Tarea[]
  puedeMover: boolean
  nombreDe: (id: number | null) => string | undefined
  onAbrir: (id: number) => void
  onMover: (id: number, estado: EstadoTarea) => void
  /** La que tiene la ficha abierta, para que se vea de cual habla. */
  seleccionada?: number | null
}

export function ListaTareas({
  tareas,
  puedeMover,
  nombreDe,
  onAbrir,
  onMover,
  seleccionada = null,
}: Props) {
  const borde = { borderColor: "var(--border)" }
  const hoy = hoyLocal()

  return (
    <Tabla etiqueta="Tareas ordenadas por vencimiento y prioridad, como las devuelve el servidor">
      <Cabecera>
        <Th>Código</Th>
        <Th>Título</Th>
        <Th>Tipo</Th>
        <Th>Prioridad</Th>
        <Th>Responsable</Th>
        <Th>Vence</Th>
        <Th derecha>Edad</Th>
        <Th>Estado</Th>
        {puedeMover && <Th />}
      </Cabecera>
      <Cuerpo>
        {enArbol(tareas).map(({ tarea: t, hija, ultima }, i, filas) => {
          const vencida = t.vence_en !== null && t.cerrado_en === null && t.vence_en < hoy
          const dias = diasDeVida(t)
          return (
            <Fila
              key={t.id}
              data-testid={`fila-${t.id}`}
              aria-current={t.id === seleccionada ? true : undefined}
              // Se calla el separador de esta fila cuando la siguiente cuelga de
              // ella o de su misma madre: dentro de una familia no hay raya.
              pegadaALaSiguiente={filas[i + 1]?.hija === true}
              className={t.id === seleccionada ? "bg-[var(--surface-elevated)]" : undefined}
            >
              <Td numero style={{ color: "var(--fg-muted)" }}>
                {t.codigo}
              </Td>
              <Td className={hija ? "relative" : undefined}>
                {/* ⚠ El codo se dibuja sobre la CELDA, no dentro del renglón de
                    texto. Colgado del texto, su alto dependía de cuánto ocupara el
                    título y la línea se quedaba a medias en cuanto una fila crecía;
                    aquí va de borde a borde de la celda y siempre llega. */}
                {hija && (
                  <span aria-hidden={true} className="pointer-events-none absolute inset-0">
                    <span
                      className="absolute left-4 top-0 border-l-2"
                      style={{
                        borderColor: "var(--border)",
                        // Hasta el codo si es la última; hasta abajo si quedan
                        // hermanas, para que el tronco siga bajando hacia ellas.
                        height: ultima ? "1.25rem" : "100%",
                      }}
                    />
                    <span
                      className="absolute left-4 top-0 h-5 w-2.5 rounded-bl-[6px] border-b-2 border-l-2"
                      style={{ borderColor: "var(--border)" }}
                    />
                  </span>
                )}
                <span className={`flex items-baseline gap-1 ${hija ? "pl-8" : ""}`}>
                  {/* El codo del organigrama, con CSS y no con un icono: la línea
                      tiene que llegar hasta el borde de la fila de arriba para que se
                      lea como una rama, y un glifo se queda dentro de su renglón.
                      `ultima` cierra el tronco — sin eso, la vertical sigue bajando
                      hacia una hija que no existe. */}
                  {hija && <span className="sr-only">Subtarea de otra tarea. </span>}
                  <button
                    type="button"
                    data-testid={`abrir-fila-${t.id}`}
                    onClick={() => onAbrir(t.id)}
                    className="text-left underline-offset-2 hover:underline"
                    style={{
                      textDecoration: t.cerrado_en !== null ? "line-through" : undefined,
                    }}
                  >
                    {t.titulo}
                  </button>
                </span>
              </Td>
              <Td style={{ color: "var(--fg-muted)" }}>{ETIQUETA_TIPO.get(t.tipo) ?? t.tipo}</Td>
              <Td>
                <Insignia tono={TONO_PRIORIDAD[t.prioridad]}>{t.prioridad}</Insignia>
              </Td>
              <Td style={{ color: "var(--fg-muted)" }}>
                {t.asignado_a === null ? "Sin asignar" : (nombreDe(t.asignado_a) ?? "—")}
              </Td>
              <Td numero style={{ color: vencida ? "var(--destructive)" : "var(--fg-muted)" }}>
                {t.vence_en ? comoFecha(t.vence_en) : "—"}
                {vencida && " · vencida"}
              </Td>
              <Td derecha numero style={{ color: "var(--fg-muted)" }}>
                {t.cerrado_en === null ? `${dias} d` : "—"}
              </Td>
              <Td className="text-xs">{t.estado.replaceAll("_", " ")}</Td>
              {puedeMover && (
                <Td>
                  <select
                    aria-label={`Mover a otro estado la tarea ${t.codigo}`}
                    value={t.estado}
                    onChange={(e) => onMover(t.id, e.target.value as EstadoTarea)}
                    className="h-8 rounded-[var(--radius-control)] border px-2 text-xs"
                    style={{ ...borde, backgroundColor: "var(--background)" }}
                  >
                    {ESTADOS.map((e) => (
                      <option key={e} value={e}>
                        {e.replaceAll("_", " ")}
                      </option>
                    ))}
                  </select>
                </Td>
              )}
            </Fila>
          )
        })}
      </Cuerpo>
    </Tabla>
  )
}
